import { useState, useRef, useEffect } from 'react';
import analyzeAgainOptions from './AnalyzeAgainOptions';
import Popup from '@mdc/ui/src/common/popup/Popup';
import { Checkbox } from '@mdc/ui';
import { ADVANCED_OPTIONS_DEFAULTS, ANALYZE_AGAIN_OPTIONS, MULTISCAN_LAST_ANALYZE_AGAIN_OPTIONS_KEY } from '@mdc/constants';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { logService } from '@mdc/services';

import './AnalyzeAgainOptionsPopup.scss';

const RULE_PARAM = 'rule';
const SANDBOX_GROUP_KEYS = ['sandbox', 'sandbox_timeout', 'sandbox_browser'];
const SANDBOX_DEFAULT_VALUES = {
    sandbox: 'windows10',
    // eslint-disable-next-line camelcase
    sandbox_timeout: 'short',
    // eslint-disable-next-line camelcase
    sandbox_browser: 'os_default'
};

const AnalyzeAgainOptionsPopup = ({ onOptionsChange, disableOptions }) => {
    const [selectedOptions, setSelectedOptions] = useState(ADVANCED_OPTIONS_DEFAULTS);
    const popupTriggerRef = useRef(null);

    useEffect(() => {
        if (onOptionsChange) {
            if (disableOptions?.length) {
                let cloneSelectedOptions = { ...selectedOptions };
                disableOptions.forEach((option) => {
                    if (Object.hasOwnProperty.call(cloneSelectedOptions, option)) {
                        delete cloneSelectedOptions[option];
                    }
                    if (cloneSelectedOptions[RULE_PARAM]?.includes(option)) {
                        cloneSelectedOptions[RULE_PARAM] = cloneSelectedOptions[RULE_PARAM].filter((opt) => opt !== option);
                    }
                });
                onOptionsChange(cloneSelectedOptions);
                return;
            }
            onOptionsChange(selectedOptions);
        }
    }, [selectedOptions, disableOptions]);

    const onOptionClick = (param, value) => {
        let currentOptions = {};
        const setOptions = (optionsObj) => {
            Object.keys(optionsObj).forEach((key) => {
                if (!optionsObj[key]) {
                    delete optionsObj[key];

                    // remove `sandbox_timeout` and `sandbox_browser` if `sandbox` is not selected
                    if (param === 'sandbox') {
                        SANDBOX_GROUP_KEYS.forEach((key) => {
                            if (optionsObj[key]) {
                                delete optionsObj[key];
                            }
                        });
                    }
                }
            });

            // Sandbox should act like a group
            // if one option is selected, one of each should be selected
            if (Object.keys(SANDBOX_DEFAULT_VALUES).includes(param) && Object.keys(optionsObj).includes('sandbox')) {
                const sandboxKeys = Object.keys(SANDBOX_DEFAULT_VALUES).filter((key) => {
                    return !Object.keys(optionsObj).some((optionKey) => {
                        return key === optionKey;
                    });
                });

                sandboxKeys.forEach((key) => {
                    const defaultValue = SANDBOX_DEFAULT_VALUES[key];
                    if (defaultValue) {
                        optionsObj[key] = defaultValue;
                    }
                });
            } else {
                // if any other value than sandbox is selected, select all sandbox options
                if (param !== 'sandbox' && Object.keys(SANDBOX_DEFAULT_VALUES).includes(param)) {
                    const selectedCheckbox = optionsObj[param];
                    Object.keys(SANDBOX_DEFAULT_VALUES).forEach((key) => {
                        const defaultValue = key !== param ? SANDBOX_DEFAULT_VALUES[key] : selectedCheckbox;
                        if (defaultValue) {
                            optionsObj[key] = defaultValue;
                        }
                    });
                }
            }

            window.sessionStorage.setItem(ANALYZE_AGAIN_OPTIONS, JSON.stringify(optionsObj));
            setSelectedOptions(Object.assign({}, optionsObj));
        };

        // eslint-disable-next-line no-prototype-builtins
        if (selectedOptions.hasOwnProperty(param)) {
            if (param === RULE_PARAM) {
                const currentRuleParams = selectedOptions[RULE_PARAM].split(',');

                if (currentRuleParams.indexOf(value) > -1) {
                    currentOptions[RULE_PARAM] = currentRuleParams.filter((ruleParamValue) => {
                        return ruleParamValue !== value;
                    }).join(',');

                    // If disable "multiscan", also disable other "rule" values. And store last them "rule" values
                    if (value === 'multiscan') {
                        currentOptions[MULTISCAN_LAST_ANALYZE_AGAIN_OPTIONS_KEY] = currentRuleParams.filter((ruleParamValue) => {
                            return ruleParamValue !== value && ruleParamValue !== 'cdr';
                        }).join(',');
                        currentOptions[RULE_PARAM] = currentRuleParams.filter((ruleParamValue) => ruleParamValue === 'cdr').join(',');
                    }
                } else {
                    currentRuleParams.push(value);

                    // If re-enable "multiscan", restore last "rule" values
                    if (value === 'multiscan') {
                        const lastOptions = JSON.parse(window.sessionStorage.getItem(ANALYZE_AGAIN_OPTIONS) || '{}');
                        const lastRuleParamsStorage = lastOptions && lastOptions[MULTISCAN_LAST_ANALYZE_AGAIN_OPTIONS_KEY]?.trim();
                        lastRuleParamsStorage && currentRuleParams.push(lastRuleParamsStorage);
                    }

                    currentOptions[RULE_PARAM] = currentRuleParams.join(',');
                }
                setOptions(Object.assign({}, selectedOptions, currentOptions));
                return;
            }

            if (selectedOptions[param] === value) {
                currentOptions[param] = '';
            } else {
                currentOptions[param] = value;
            }
        } else {
            currentOptions[param] = value;
            // If re-enable "multiscan", restore last "rule" values
            if (param === RULE_PARAM && value === 'multiscan') {

                try {
                    const lastOptions = JSON.parse(window.sessionStorage.getItem(ANALYZE_AGAIN_OPTIONS) || '{}');
                    const lastRuleParamsStorage = lastOptions && lastOptions[MULTISCAN_LAST_ANALYZE_AGAIN_OPTIONS_KEY]?.trim();
                    const lastRuleParams = lastRuleParamsStorage ? lastRuleParamsStorage.split(',') : [];
                    currentOptions[param] = [value].concat(lastRuleParams).join(',');
                } catch (error) {
                    logService.error(error);
                }


            }
        }

        setOptions(Object.assign({}, selectedOptions, currentOptions));
    };

    const { t } = useTranslation();

    const isOptionSelected = ({ param, value }) => {
        // eslint-disable-next-line no-prototype-builtins
        if (selectedOptions.hasOwnProperty(param)) {
            if (selectedOptions[param].indexOf(value) > -1) {
                return true;
            }

            if (Array.isArray(value)) {
                return value.map((valueObj) => {
                    if (Array.isArray(valueObj)) {
                        return valueObj.map((newValueObj) => {
                            if (selectedOptions[param] === valueObj[newValueObj].key) {
                                return true;
                            }
                        });
                    }
                });
            }

        }
        return false;
    };

    const getSubOptionsList = (options, onOptionClick, disableOptions) => {
        const subOptionItems = options.filter((option) => {
            return Array.isArray(option.value) && !disableOptions?.includes(option.param);
        });

        return subOptionItems.map((option) => {
            const optionValue = option.value;
            if (Array.isArray(optionValue)) {
                const checkboxes = optionValue.map((valueObj, index) => {
                    const subOptions = valueObj.values.map((newValueObj) => {
                        const isDisabled = option.disabled && option.disabled === 'true';
                        const isSelected = isOptionSelected({ param: valueObj.param, value: newValueObj.key });
                        const label = newValueObj.label ? newValueObj.label : t(newValueObj?.translationKey);

                        return (
                            <Checkbox
                                isDisabled={isDisabled}
                                isSelected={isSelected}
                                key={newValueObj.key}
                                onChange={onOptionClick.bind(null, valueObj.param, newValueObj.key)}
                                useManualCheck
                            >
                                {label}
                            </Checkbox>
                        );
                    });

                    return (
                        <div key={index}>
                            <span className='subOptionFieldName'>{t(valueObj?.fieldName)}</span>
                            {subOptions}
                        </div>
                    );
                });

                return (
                    <div
                        className="subOption"
                        key={option.param}
                    >
                        <span className="options">
                            {checkboxes}
                        </span>
                    </div>
                );
            }

            return null;
        });
    };

    const subOptionsList = getSubOptionsList(analyzeAgainOptions, onOptionClick, disableOptions);

    const subOptionsListFragment = subOptionsList.length ? (
        <div className='subOption'>
            {subOptionsList}
        </div>
    ) : null;

    const getOptionsList = (options, onOptionClick, disableOptions) => {
        return options.filter((option) => option.param !== RULE_PARAM || option.disabled || option.value === 'cdr').map((option) => {
            const getValue = ({ param, value }) => {
                if (Array.isArray(value) && param !== RULE_PARAM) {
                    // eslint-disable-next-line no-prototype-builtins
                    if (selectedOptions.hasOwnProperty(param)) {
                        return selectedOptions[param];
                    }

                    if (param === 'sandbox') {
                        return value[0].values[0].key;
                    }

                    return value[0].key;
                }
                return value;
            };
            const value = getValue(option);
            const forcedDisable = disableOptions?.includes(option.param) || disableOptions?.includes(value);
            const isDisabled = (option.disabled && option.disabled === 'true') || !!forcedDisable;
            const isSelected = isOptionSelected(option) && !forcedDisable;
            const hasSuboptions = Array.isArray(option.value);

            const label = value === 'dlp' ?
                (<a
                    href='https://onlinehelp.opswat.com/mdcloud/5._Homepage.html'
                    rel="noopener noreferrer"
                    target='_blank'
                >
                    {t(option.translationKey)}
                </a>) :
                t(option.translationKey);

            return (
                <Checkbox
                    isDisabled={isDisabled}
                    isSelected={isSelected}
                    key={option.translationKey}
                    onChange={onOptionClick.bind(null, option.param, value)}
                    hasSuboptions={hasSuboptions}
                    useManualCheck
                >
                    {label}
                </Checkbox>
            );
        });
    };

    const getMultiScanOptionsList = (options, onOptionClick, disableOptions) => {
        return options.filter((option) => option.param === RULE_PARAM && !option.disabled && option.value !== 'cdr').map((option) => {
            const forcedDisable = disableOptions?.includes(option.param) || disableOptions?.includes(option.value);
            const isMultiScanSelected = isOptionSelected({ param: 'rule', value: 'multiscan' });
            let isSelected = isOptionSelected(option) && !forcedDisable;

            if (!isMultiScanSelected && option.value !== 'multiscan') {
                try {
                    const analyzedAgainOptions = JSON.parse(window.sessionStorage.getItem(ANALYZE_AGAIN_OPTIONS) || '{}');
                    isSelected = analyzedAgainOptions[MULTISCAN_LAST_ANALYZE_AGAIN_OPTIONS_KEY]?.split(',')?.includes(option.value);
                } catch (error) {
                    logService.error(error);
                }

            }

            const isDisabled = (!isMultiScanSelected && option.value !== 'multiscan') || !!forcedDisable;

            return (
                <Checkbox
                    isDisabled={isDisabled}
                    isSelected={isSelected}
                    key={option.translationKey}
                    onChange={onOptionClick.bind(null, option.param, option.value)}
                    useManualCheck
                >
                    {t(option.translationKey)}
                </Checkbox>
            );
        });
    };

    const optionsList = getOptionsList(analyzeAgainOptions, onOptionClick, disableOptions);
    const multiScanOptionsList = getMultiScanOptionsList(analyzeAgainOptions, onOptionClick, disableOptions);

    const optionListOrdered = optionsList.reduce((acc, item) => {
        if (item.props.isDisabled === true && item.props.hasSuboptions === false) {
            acc.disabled.push(item);
        } else if (item.props.hasSuboptions === true) {
            acc.withSuboptions.push(item);
        } else {
            acc.enabled.push(item);
        }
        return acc;
    }, { disabled: [], enabled: [], withSuboptions: [] });

    return <span className="advancedIconButton">
        <i className='icon icon-settings' ref={popupTriggerRef}/>
        <Popup className="analyzeAgainAdvancedOptionsPopup" triggerRef={popupTriggerRef}>
            <div className='advancedOptionsColumn'>
                {optionListOrdered.disabled}
                <hr/>
                {multiScanOptionsList}
                {!!optionListOrdered.enabled.length && <hr/>}
                {optionListOrdered.enabled}
            </div>
            <div className='advancedOptionsColumn'>
                {optionListOrdered.withSuboptions}
                {subOptionsListFragment}
            </div>
        </Popup>
    </span>;
};

AnalyzeAgainOptionsPopup.defaultProps = {
    disableOptions: []
};

AnalyzeAgainOptionsPopup.propTypes = {
    onOptionsChange: PropTypes.func,
    disableOptions: PropTypes.array
};

export default AnalyzeAgainOptionsPopup;
